// Shrinks photos in the browser before upload. The server's nginx refuses any
// request body over 1 MB (HTTP 413), so a photo is scaled down and re-encoded
// as JPEG, trying lower quality and then smaller sizes until it fits a byte
// budget.
//
// The size rules (budget, scaling, step order) are plain functions so they can
// be unit tested; only compressDrawable and compressImage touch the canvas.

/** The server's nginx limit: 1 MB per request body. */
export const MAX_REQUEST_BYTES = 1024 * 1024;
/** What all photos in one request may add up to. The rest of the 1 MB is
 *  headroom for the multipart part headers. */
export const REQUEST_PHOTOS_BUDGET_BYTES = 880 * 1024;
/** Cap for a photo that is sent in a request of its own. */
export const SINGLE_PHOTO_BUDGET_BYTES = 850 * 1024;

/** Longest side to try, largest first. */
export const MAX_SIDES = [1280, 1024, 800, 640] as const;
/** JPEG quality to try at each size, highest first. */
export const QUALITIES = [0.7, 0.6, 0.5, 0.4] as const;

export interface CompressionStep {
  width: number;
  height: number;
  quality: number;
}

export interface CompressedImage extends CompressionStep {
  dataUrl: string;
  bytes: number;
  /** False when even the smallest step was over budget. */
  fits: boolean;
}

/** Byte budget for each photo when `photosPerRequest` photos go in one request. */
export const photoBudgetBytes = (photosPerRequest: number): number => {
  const count = Math.max(1, Math.floor(photosPerRequest));
  return Math.min(SINGLE_PHOTO_BUDGET_BYTES, Math.floor(REQUEST_PHOTOS_BUDGET_BYTES / count));
};

/** Scales width × height down so the longest side is at most maxSide. Never scales up. */
export const fitWithin = (
  width: number,
  height: number,
  maxSide: number
): { width: number; height: number } => {
  const longest = Math.max(width, height);
  if (longest <= maxSide) return { width, height };
  const scale = maxSide / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/** The order to try: every quality at 1280px, then at 1024px, 800px and 640px.
 *  Sizes that come out the same (a photo already smaller than 1280px) are tried once. */
export const compressionSteps = (width: number, height: number): CompressionStep[] => {
  const steps: CompressionStep[] = [];
  const seen = new Set<string>();
  for (const maxSide of MAX_SIDES) {
    const size = fitWithin(width, height, maxSide);
    const key = `${size.width}x${size.height}`;
    if (seen.has(key)) continue;
    seen.add(key);
    for (const quality of QUALITIES) steps.push({ ...size, quality });
  }
  return steps;
};

/** Decoded byte size of a base64 data URL. */
export const dataUrlBytes = (dataUrl: string): number => {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
};

/** Encodes each step in order and returns the first result within the budget.
 *  If none fits, returns the smallest result with fits = false. */
export const encodeUntilFits = (
  steps: CompressionStep[],
  encode: (step: CompressionStep) => string,
  budgetBytes: number
): CompressedImage => {
  let smallest: CompressedImage | null = null;
  for (const step of steps) {
    const dataUrl = encode(step);
    const bytes = dataUrlBytes(dataUrl);
    const result: CompressedImage = { ...step, dataUrl, bytes, fits: bytes <= budgetBytes };
    if (result.fits) return result;
    if (!smallest || bytes < smallest.bytes) smallest = result;
  }
  if (!smallest) throw new Error('Nothing to compress.');
  return smallest;
};

/** Compresses anything a canvas can draw (an image, a video frame, a canvas). */
export const compressDrawable = (
  source: CanvasImageSource,
  width: number,
  height: number,
  budgetBytes: number
): CompressedImage => {
  if (!width || !height) throw new Error('The image is empty.');
  const canvas = document.createElement('canvas');
  let drawn = '';
  const encode = (step: CompressionStep): string => {
    const size = `${step.width}x${step.height}`;
    if (size !== drawn) {
      canvas.width = step.width;
      canvas.height = step.height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas is not available.');
      // JPEG has no transparency: paint white so transparent areas don't turn black.
      context.fillStyle = '#fff';
      context.fillRect(0, 0, step.width, step.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(source, 0, 0, step.width, step.height);
      drawn = size;
    }
    return canvas.toDataURL('image/jpeg', step.quality);
  };
  try {
    return encodeUntilFits(compressionSteps(width, height), encode, budgetBytes);
  } finally {
    // Free the pixel memory straight away (mobile Safari caps total canvas memory).
    canvas.width = 0;
    canvas.height = 0;
  }
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not read the image.'));
    image.src = src;
  });

/** Decodes an image file, blob or data URL and compresses it to the budget. */
export const compressImage = async (
  source: Blob | string,
  budgetBytes: number
): Promise<CompressedImage> => {
  const src = typeof source === 'string' ? source : URL.createObjectURL(source);
  try {
    const image = await loadImage(src);
    return compressDrawable(image, image.naturalWidth, image.naturalHeight, budgetBytes);
  } finally {
    if (typeof source !== 'string') URL.revokeObjectURL(src);
  }
};
